import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {whenRouterHasNavigatedTo, withLatestFromSelectors} from '@store/common/effects.helpers';
import {catchError, filter, map, of, switchMap} from 'rxjs';
import {HoldsActions} from './holds.actions';
import {PaginationSelectors, RouterSelectors, SnackBarActions} from '@store/aviary/aviary.adapter';
import {HoldsService} from './holds.service';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {exhaustMap, mergeMap} from 'rxjs/operators';
import {openConfirmationDialog, openFormDialog, openNotificationDialog} from '@aviary/store/common/effect-dialog.helpers';
import {PlaceHoldBranchSelectionDialog, PlaceHoldBranchSelectionValue} from '@app/my-account/holds/hold-actions/place-hold-branch-selection.dialog';
import {EditHoldDialog} from '@app/my-account/holds/hold-actions/edit-hold.dialog';
import {CancelHoldDialog} from '@app/my-account/holds/hold-actions/cancel-hold.dialog';
import {EditHoldValue, PauseAllHoldsValue} from '@store/holds/holds.types';
import {asArray, isDefined, isPopulatedArray, isPopulatedString} from '@aviary/store/common/typing.helpers';
import {CatalogSelectors} from '@store/catalog/catalog.selectors';
import {PatronActions} from '@store/patron/patron.actions';
import {HoldsSelectors} from '@store/holds/holds.selectors';
import {PauseAllHoldsDialog} from '@app/my-account/holds/hold-actions/pause-all-holds.dialog';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {SimpleNotificationDialog, SimpleNotificationDialogData} from '@aviary/dialogs/simple-notification/simple-notification.dialog';
import {Store} from '@ngrx/store';
import {concatLatestFrom} from '@ngrx/operators';
import {CatalogService} from '@store/catalog/catalog.service';
import {CheckoutsSelectors} from '@store/checkouts/checkouts.selectors';
import {ItemRequestsSelectors} from '@store/item-requests/item-requests.selectors';

@Injectable({providedIn: 'root'})
export class HoldsEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly service: HoldsService = inject(HoldsService);
  private readonly catalogService: CatalogService = inject(CatalogService);
  private readonly formatPipe: FormatPipe = inject(FormatPipe);
  private readonly store: Store = inject(Store);

  public loadReadyForPickupHoldsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('HOLDS', 'DASHBOARD'),
      map(() => HoldsActions.loadReadyForPickupHolds())
    )
  );

  public loadOnHoldHoldsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('HOLDS'),
      map(() => HoldsActions.loadOnHoldHolds())
    )
  );

  public loadReadyForPickupHolds = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.loadReadyForPickupHolds),
      switchMap(() => this.service.loadReadyForPickupHolds().pipe(
        map(response => HoldsActions.loadReadyForPickupHoldsSuccess(response)),
        catchError((error: unknown) => of(HoldsActions.loadReadyForPickupHoldsFailure(error)))
      ))
    )
  );

  public loadOnHoldHolds = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.loadOnHoldHolds),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([, queryParams]) => this.service.loadOnHoldHolds(queryParams).pipe(
        map(response => HoldsActions.loadOnHoldHoldsSuccess(response)),
        catchError((error: unknown) => of(HoldsActions.loadOnHoldHoldsFailure(error)))
      ))
    )
  );

  public loadHoldsForCatalogRecords = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.loadHoldsForCatalogRecords),
      withLatestFromSelectors(PatronSelectors.Auth.hasPatron),
      filter(([, hasPatron]) => hasPatron),
      switchMap(([{catalogRecordIds}]) =>
        isPopulatedArray(catalogRecordIds)
          ? this.service.loadHoldsForCatalogRecords(catalogRecordIds).pipe(
            map(response => HoldsActions.loadHoldsForCatalogRecordsSuccess(response)),
            catchError((error: unknown) => of(HoldsActions.loadHoldsForCatalogRecordsFailure(error)))
          )
          : of(HoldsActions.loadHoldsForCatalogRecordsSuccess([])) // No catalog records?  We know there are no holds, so don't bother the service/API.
      )
    )
  );

  // Get the full catalog record object via the catalogRecordId, then start the Place Hold process
  public placeHoldByCatalogRecordId = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.placeHoldByCatalogRecordId),
      switchMap(({catalogRecordId}) => this.catalogService.loadCatalogRecord(catalogRecordId).pipe(
        map(catalogRecord => HoldsActions.placeHoldOrLogin(catalogRecord)),
        catchError((error: unknown) => of(HoldsActions.placeHoldFailure(error)))
      ))
    )
  );

  public placeHoldOrLogin = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.placeHoldOrLogin),
      withLatestFromSelectors(PatronSelectors.Auth.hasPatron),
      map(([{catalogRecord}, hasPatron]) => hasPatron ?
        HoldsActions.preparePlaceHold(catalogRecord) :
        PatronActions.showLoginDialog(catalogRecord))
    )
  );

  public preparePlaceHold = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.preparePlaceHold),
      withLatestFromSelectors(PatronSelectors.Auth.oneClickHoldsEnabled, PatronSelectors.Auth.branchId),
      map(([{catalogRecord}, oneClickHoldsEnabled, branchId]) => oneClickHoldsEnabled ?
        HoldsActions.placeHold(catalogRecord, branchId) : HoldsActions.showPlaceHoldBranchSelection(catalogRecord))
    )
  );

  public showPlaceHoldBranchSelection = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.showPlaceHoldBranchSelection),
      withLatestFromSelectors(PatronSelectors.Auth.branchId),
      openFormDialog(([{catalogRecord}, defaultBranchId]) => ({
        componentClass: PlaceHoldBranchSelectionDialog,
        data: {catalogRecord, defaultBranchId},
        onConfirm: (formValue: PlaceHoldBranchSelectionValue) => HoldsActions.placeHold(catalogRecord, formValue.pickupBranch)
      }))
    )
  );

  public placeHold = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.placeHold),
      exhaustMap(({catalogRecord, pickupBranchId}) => this.service.placeHold(catalogRecord.id, pickupBranchId).pipe(
        mergeMap((hold) => ([
          SnackBarActions.showSuccessWithRouterLink({
            beforeLinkText: 'Hold placed successfully.',
            linkText: 'View Holds',
            routeName: 'HOLDS'
          }),
          HoldsActions.placeHoldSuccess(hold)
        ])),
        catchError((error: unknown) => of(
          error?.['error']?.['errors']?.[0]?.code === 'BRANCH_INVALID_ID' ?
            HoldsActions.showPlaceHoldBranchSelection(catalogRecord) :
            HoldsActions.placeHoldFailure(error))
        )
      ))
    )
  );

  // Get the Hold object via the catalogRecordId, then show the Edit Hold dialog
  public editHoldByCatalogRecordId = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.editHoldByCatalogRecordId),
      concatLatestFrom(({catalogRecordId}) => this.store.select(HoldsSelectors.HoldsForCatalogRecords.singleHold(catalogRecordId))),
      map(([, hold]) => HoldsActions.showEditHold(hold))
    )
  );

  public showEditHold = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.showEditHold),
      openFormDialog(({hold}) => ({
        componentClass: EditHoldDialog,
        data: {hold},
        onConfirm: (formValue: EditHoldValue) => {
          return hold.pickupBranchId !== formValue.pickupBranch ?
            HoldsActions.editHoldBranch(hold, formValue) :
            HoldsActions.editHoldPause(hold, formValue);
        }
      }))
    )
  );

  public editHoldBranch = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.editHoldBranch),
      exhaustMap(({hold, value}) => this.service.updateHoldPickupBranch(hold.id, value.pickupBranch).pipe(
        mergeMap(() => {
          return ((hold.notWantedBefore ?? '') === (value.pauseUntil ?? '')) ? [
            SnackBarActions.showSuccess('Hold has been updated.'),
            HoldsActions.editHoldSuccess()
          ] : [HoldsActions.editHoldPause(hold, value)]
        }),
        catchError((error: unknown) => of(
          HoldsActions.editHoldFailure(error))
        )
      ))
    )
  );

  public editHoldPause = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.editHoldPause),
      concatLatestFrom(({hold}) => this.store.select(HoldsSelectors.Stateless.itemTitleFor(hold))),
      exhaustMap(([{hold, value}, title]) => (isPopulatedString(value.pauseUntil) ?
          this.service.pauseHold([hold.id], value.pauseUntil) :
          this.service.resumeHolds([hold.id])
      ).pipe(
        mergeMap(() => ([
          SnackBarActions.showSuccess(`Your hold on ${title} has been ${isPopulatedString(value.pauseUntil) ? 'updated' : 'resumed'}.`),
          HoldsActions.editHoldSuccess()
        ])),
        catchError((error: unknown) => of(
          HoldsActions.editHoldFailure(error)),
        )
      ))
    )
  );

  public showCancelHold = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.showCancelHold),
      openConfirmationDialog(({hold}) => ({
        componentClass: CancelHoldDialog,
        data: {hold},
        onConfirm: () => HoldsActions.cancelHold(hold)
      }))
    )
  );

  public cancelHold = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.cancelHold),
      exhaustMap(({hold}) => this.service.cancelHolds([hold.id]).pipe(
        mergeMap(() => ([
          SnackBarActions.showSuccess('Hold has been canceled.'),
          HoldsActions.cancelHoldSuccess(hold)
        ])),
        catchError((error: unknown) => of(
          HoldsActions.cancelHoldFailure(error))
        )
      ))
    )
  );

  public reloadHoldsAfterChange = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.placeHoldSuccess, HoldsActions.editHoldSuccess, HoldsActions.cancelHoldSuccess),
      withLatestFromSelectors(RouterSelectors.routeName),
      concatLatestFrom(([, routeName]) => {
        switch (routeName) {
          case 'CATALOG_BIB_DETAILS':
            return this.store.select(CatalogSelectors.Current.id).pipe(map(asArray));
          case 'CATALOG_SEARCH':
            return this.store.select(CatalogSelectors.List.recordIds);
          case 'FAVORITES':
            return this.store.select(CatalogSelectors.Favorites.ids);
          case 'BORROWING_HISTORY':
            return this.store.select(CheckoutsSelectors.BorrowingHistory.catalogRecordIds);
          case 'ITEM_REQUESTS':
            return this.store.select(ItemRequestsSelectors.ClosedItemRequests.linkedCatalogRecordIds);
          default:
            return of(undefined);
        }
      }),
      mergeMap(([[, routeName], catalogRecordIds]) => [
        ...routeName === 'HOLDS' ? [HoldsActions.loadOnHoldHolds()] : [],
        ...isDefined(catalogRecordIds) ? [HoldsActions.loadHoldsForCatalogRecords(catalogRecordIds)] : []
      ])
    )
  );

  public showPauseAllHolds = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.showPauseAllHolds),
      withLatestFromSelectors(
        HoldsSelectors.OnHold.holds,
        PaginationSelectors.totalCount
      ),
      openFormDialog(([, holds, totalHoldsCount]) => ({
        componentClass: PauseAllHoldsDialog,
        data: {holds, totalHoldsCount},
        onConfirm: (value: PauseAllHoldsValue) => HoldsActions.pauseAllHolds(value.pauseUntil)
      }))
    )
  );

  public pauseAllHolds = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.pauseAllHolds),
      switchMap(({pauseUntil}) => this.service.pauseAllHolds(pauseUntil).pipe(
        mergeMap((response) => {
          if (response.notPausedCount > 0) {
            return [HoldsActions.showPartiallyPausedHoldsConfirmation()]
          }
          return [
            HoldsActions.pauseAllHoldsSuccess(),
            SnackBarActions.showSuccess(`All eligible items successfully paused until ${this.formatPipe.transform(pauseUntil, 'DATE_ONLY')}.`)
          ]
        }),
        catchError((error: unknown) => of(HoldsActions.pauseAllHoldsFailure(error)))
      ))
    )
  );

  public showPartiallyPausedHoldsConfirmation = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.showPartiallyPausedHoldsConfirmation),
      openNotificationDialog(() => ({
        componentClass: SimpleNotificationDialog,
        data: {
          title: 'Some Items Paused',
          message: 'Some items were not eligible to pause because they are already en route to your library.',
          confirmButtonLabel: 'Close'
        } as SimpleNotificationDialogData,
        onConfirm: () => HoldsActions.pauseAllHoldsSuccess()
      }))
    )
  );

  public pauseAllHoldsSuccess = createEffect(() =>
    this.actions.pipe(
      ofType(HoldsActions.pauseAllHoldsSuccess),
      map(() => HoldsActions.loadOnHoldHolds())
    )
  );
}
